import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Mobile menu: ONE button that sits in the header and then sticks to the
  // top-right corner when the header scrolls away (no cross-fade, no jump)
  const anchorRef = useRef<HTMLDivElement>(null);
  const mobileBtnRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useLayoutEffect(() => {
    // px from the top edge once the header has scrolled away (never lower than
    // the button's own spot in the header, so it doesn't shift on pages where it sits higher)
    let stickTop = 16;
    let anchorTop = 0;
    let anchorRight = 16;
    let frame = 0;

    const measure = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      anchorTop = rect.top + window.scrollY;
      anchorRight = window.innerWidth - rect.right;
      stickTop = Math.min(16, anchorTop);
    };

    const position = () => {
      frame = 0;
      const btn = mobileBtnRef.current;
      if (!btn) return;
      const y = Math.max(window.scrollY, 0); // ignore iOS overscroll bounce
      const top = Math.max(stickTop, anchorTop - y);
      btn.style.transform = `translate3d(0, ${top}px, 0)`;
      btn.style.right = `${anchorRight}px`;
      setIsStuck(anchorTop - y < stickTop);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(position);
    };
    const onResize = () => {
      measure();
      position();
    };

    measure();
    position();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [location.pathname]);

  const navigation = [
    { name: "Hem", href: "/" },
    { name: "Tjänster", href: "/services" },
    { name: "Våra projekt", href: "/portfolio" },
    { name: "Om oss", href: "/about#team" },
    { name: "Kontakt", href: "/contact" },
  ];

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (window.location.hash) {
        navigate("/", { replace: true });
      }
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const isNavActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href.split("#")[0];
  };

  const mobileMenuLinks = (
    <div className="space-y-2">
      {navigation.map((item) => (
        <Link
          key={item.name}
          to={item.href}
          onClick={() => setIsMenuOpen(false)}
          className={cn(
            "block bg-white rounded-lg shadow-md px-2 py-3 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg text-center min-h-[48px] flex items-center justify-center",
            isNavActive(item.href) ? "text-primary shadow-lg" : "text-foreground"
          )}
        >
          <span className="text-sm font-semibold">{item.name}</span>
        </Link>
      ))}
    </div>
  );

  return (
    <header
      className={cn(
        "relative z-50 w-full",
        isHome
          ? "absolute top-4 left-0 right-0 md:top-6 bg-transparent"
          : "bg-background border-b border-border/40 shadow-sm md:pt-6"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 md:px-6">
        <div
          className={cn(
            "flex justify-between items-center h-16 md:h-14",
            "md:bg-card/95 md:backdrop-blur-md md:border md:border-border/80",
            "md:rounded-full md:shadow-lg md:px-6 md:max-w-4xl md:mx-auto",
            !isHome && "bg-card border border-border/60 rounded-2xl shadow-md px-4"
          )}
        >
          <Link
            to="/"
            onClick={handleLogoClick}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200 shrink-0"
            aria-label="Gå till startsidan"
          >
            <Logo size="lg" variant="default" />
          </Link>

          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8" aria-label="Huvudnavigation">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "transition-colors duration-200 font-medium relative group",
                  isNavActive(item.href)
                    ? "text-primary"
                    : "text-foreground hover:text-primary"
                )}
              >
                {item.name}
                <span
                  className={cn(
                    "absolute bottom-0 left-0 h-0.5 transition-all duration-200 group-hover:w-full",
                    isNavActive(item.href) ? "w-full bg-primary" : "w-0 bg-accent"
                  )}
                />
              </Link>
            ))}
          </nav>

          {/* Mobile menu: placeholder that keeps the button's spot in the header */}
          <div ref={anchorRef} className="md:hidden h-10 w-10" aria-hidden="true" />
        </div>

        {/* Mobile menu button: one element, follows the header then sticks */}
        <div
          ref={mobileBtnRef}
          className="md:hidden fixed top-0 z-[60] will-change-transform"
          style={{ right: 16, transform: "translate3d(0, 28px, 0)" }}
        >
          <div className="relative">
            <Button
              variant="accent"
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={cn(
                "bg-accent text-accent-foreground hover:bg-accent/90 transition-shadow duration-300",
                isStuck && "shadow-lg"
              )}
              aria-label={isMenuOpen ? "Stäng meny" : "Öppna meny"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>

            {isMenuOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                {mobileMenuLinks}
              </div>
            )}
          </div>
        </div>

        {isMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-[55] animate-in fade-in"
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </div>
    </header>
  );
};

export default Header;
